import './EULA.scss'

const EULA = () => {
  return (
    <div className="terms-wrapper">
      <div className="container">
        <h1>Comment8 End User License Agreement (EULA)</h1>

        <h3>1. Acceptance of Agreement</h3>
        <p>
          By downloading, installing, or using the Comment8 application (the “App”) or accessing any services provided
          by Comment8 (“Services”), you acknowledge that you have read, understood, and agree to be bound by this End
          User License Agreement (“EULA” or “Agreement”). If you do not agree with these terms, you must not use the
          App. This Agreement is a binding legal contract between you (“User” or “you”) and <strong>[Comment8 by Sokhey
          LLC]</strong> (“Company,” “we,” or “us”) regarding your use of the App and Services. You represent that you
          are legally competent to enter this Agreement and are at least the minimum age required by applicable law to
          use this App (13 years old in many jurisdictions, or a higher age as may be required). Parents or guardians of
          users under the age of majority are responsible for their dependents’ compliance with this EULA.
        </p>

        <h3>2. License Grant and Ownership</h3>
        <p>
          Subject to your compliance with this EULA, we grant you a personal, limited, non-exclusive, non-transferable,
          non-sublicensable, revocable license to install and use Comment8 on your compatible devices for your <strong>personal,
          non-commercial use</strong>. All rights not expressly granted to you are reserved by the Company and its
          licensors. You <strong>do not</strong> acquire any ownership interest in the App or any related intellectual
          property rights under this Agreement. The App (including all content, code, software, and branding) is owned
          by the Company or its licensors and is protected by copyright, trademark, and other laws. You agree not to
          remove, alter, or obscure any proprietary notices on the App.
        </p>
        <p>
          <strong>License Restrictions:</strong> You agree that you will not directly or indirectly do or attempt any of
          the following:
        </p>
        <ul>
          <li>Copy, modify, adapt, translate, or create derivative works of the App or any portion of it.</li>
          <li>Reverse engineer, decompile, decrypt, or disassemble the App, except to the limited extent allowed by
            applicable law (and then only with prior written notice to us).
          </li>
          <li>Rent, lease, lend, sell, sublicense, assign, distribute, publish, or otherwise transfer the App or any
            feature or part of it to any third party without our consent.
          </li>
          <li>Remove or alter any copyright, trademark, or other proprietary rights notices in the App.</li>
          <li>Use the App for any purpose that is unlawful or prohibited by this EULA or any applicable laws or
            regulations. This includes (but is not limited to) using the App in violation of export control laws or
            other legal restrictions.
          </li>
        </ul>

        <h3>3. User Accounts and Security</h3>
        <p>
          In order to use certain features of Comment8 (such as posting comments or messaging), you may need to create a
          user account. When creating an account, you agree to provide accurate, current, and complete information. You
          are responsible for maintaining the confidentiality of your account credentials and for all activities that
          occur under your account. <strong>Do not share</strong> your username or password with others, and notify us
          immediately at <strong><a href="https://comment8.ai/support">https://comment8.ai/support</a></strong> if you
          suspect any unauthorized access to or use of your account. We are not liable for
          any loss or damage arising from your failure to safeguard your account. We may require you to change your
          password if we believe your account is no longer secure.
        </p>
        <p>
          You must <strong>not</strong> create an account if you have been previously banned or removed from our
          Services for violation of this EULA or any applicable law. You also agree not to use anyone else’s account or
          allow others to use your account without permission
        </p>

        <h3>4. User Conduct and Prohibited Content</h3>
        <p>
          Comment8 is a platform that enables users to comment on content across the internet. We are committed to
          providing an open environment for discourse <strong>while ensuring a respectful, safe experience for all
          users</strong>. As a condition of using the App, you agree <strong>not</strong> to misuse the Services or
          engage in any behavior that is harmful, unlawful, or prohibited. <strong>You are solely responsible for any
          comments, messages, or other content (“User Content”) that you create, post, or share using Comment8</strong>.
        </p>
        <p>
          <strong>4.1 Prohibited Conduct:</strong> You <strong>agree not to use</strong> Comment8 (including any
          commenting features, messaging, or any part of the Services) to do any of the following:
        </p>
        <ul>
          <li><strong>Harass, abuse, or harm others:</strong> This includes engaging in or encouraging <strong>abuse,
            harassment, bullying, threats, flaming, or intimidation</strong> toward any person or organization. You may
            not use Comment8 to stalk, harass, or incite violence or hatred against anyone.
          </li>
          <li><strong>Hate Speech and Discrimination:</strong> Do not post content that is hateful, derogatory, or
            discriminatory. <strong>Any form of hate speech</strong> (e.g., racist, sexist, homophobic, or otherwise
            targeting a group or individual based on religion, gender, sexual orientation, race, ethnicity, national
            origin, or other protected characteristics) is strictly prohibited. We have zero tolerance for slurs,
            epithets, or advocating violence or prejudice against protected groups.
          </li>
          <li><strong>Illegal Activities:</strong> You must not use the Service for any unlawful purposes or to promote
            or facilitate illegal activities. <strong>Engaging in or contributing to any illegal activity</strong> (or
            encouraging others to do so) is forbidden. For example, you may not use Comment8 to plan or encourage
            criminal conduct, to buy or sell illegal drugs, or to participate in gambling if such activity is illegal in
            your jurisdiction.
          </li>
          <li><strong>Objectionable or Offensive Content:</strong> You may <strong>not post any content that is
            unlawful, offensive, threatening, libelous, defamatory, obscene or otherwise objectionable</strong>. This
            means <strong>no defamatory commentary</strong> about individuals, and no content that is excessively
            violent, graphically disturbing, or otherwise in “exceptionally poor taste”. In particular, you may not
            share:
          </li>
          <ul>
            <li><strong>Pornographic or sexually explicit material</strong>, or any content featuring nudity or sexual
              activities that is inappropriate for a general audience(Non-pornographic adult content that is allowed by
              law must be appropriately tagged and not distributed to minors.)
            </li>
            <li><strong>Graphic violence or calls for violence:</strong>Content that <strong>portrays extreme violence
              or encourages violence</strong> toward any person, group, or animal is forbidden. Do not use Comment8 to
              glorify violence, self-harm, or cruelty.
            </li>
            <li><strong>Bullying and harassment:</strong>Any form of personal attack, sustained bullying, or <strong>targeted
              harassment</strong> is not allowed (this includes urging someone to hurt themselves or “doxing” someone by
              revealing personal information).
            </li>
          </ul>
          <li><strong>False or Misleading Information:</strong> Do not spread false, misleading, or deceptive
            information. You <strong>may not provide information that is false, misleading, or inaccurate</strong> via
            our Services. This includes impersonating any person or organization, misrepresenting your identity or
            affiliation, or posting content that is intended to deceive or defraud others.
          </li>
          <li><strong>Spam and Malicious Activity:</strong> You shall not use the Service to transmit any unsolicited or
            unauthorized advertising, promotional materials, “junk mail,” “spam,” chain letters, pyramid schemes, or any
            other form of solicitation. Additionally, you may not distribute malware, viruses, or any other harmful code
            through Comment8. Any attempt to <strong>interfere with or disrupt</strong> the normal functioning of the
            App or the servers and networks connected to the App (for example, through a denial-of-service attack,
            introducing viruses, or other sabotage) is strictly prohibited.
          </li>
          <li><strong>Circumvention and Unauthorized Access:</strong> You must not attempt to circumvent any security or
            content filtering measures we employ. Do not access (or attempt to access) any part of the Service that you
            are not authorized to use, such as other users’ accounts, or our backend systems. Any form of hacking, data
            mining, scraping, or unauthorized data collection from the App is forbidden.
          </li>
          <li><strong>Other Unacceptable Use:</strong> You agree to comply with all applicable third-party platform
            rules and policies when using Comment8. <strong>Any conduct or content that is not supported by Apple App
              Store or Google Play Store policies is prohibited</strong>, even if not explicitly mentioned above. For
            example, content that <strong>Apple or Google would deem objectionable (hate speech, pornography, extreme
              violence, illegal activity, etc.) is not allowed on Comment8</strong>. We reserve the right to prohibit
            any other behavior that we deem inconsistent with the intended purpose of the platform or that could harm
            other users, the community, or the Company.
          </li>
        </ul>
        <p>
          <strong>4.2 Content Ownership and Responsibility:</strong> You retain any intellectual property rights you
          hold in the User Content you post on Comment8. However, by submitting or posting content on or through our
          Service, you grant the Company a worldwide, non-exclusive, royalty-free, sublicensable, and transferable
          license to use, reproduce, distribute, display, and perform your content as needed to operate the Service (for
          example, to display your comments to other users, to store your content on our servers, etc.). This license
          ends when you delete your content or terminate your account, <strong>except</strong> to the extent your
          content has been shared with others and they have not deleted it, or it was archived as part of our backup
          systems. You represent and warrant that you have all necessary rights to post the content you share and that
          such content does not violate any law or any third-party rights (such as privacy, publicity, copyright, or
          trademark rights).
        </p>
        <p>
          We <strong>do not endorse, guarantee the accuracy of, or take responsibility for any User
          Content</strong> posted by users. All opinions expressed by users are their own. While we do not pre-screen
          all user comments, you understand that we have the right (but not the obligation) to monitor, screen, edit, or
          remove any content posted on Comment8 at our discretion. By using the App, you may be exposed to content that
          you find objectionable or offensive, and you agree that your use of the Service is at your own risk.
        </p>

        <h3>5. Objectionable Content Policy and Enforcement</h3>
        <p>
          We reserve the right to enforce this EULA and ensure a safe community. <strong>If you violate the letter or
          spirit of this Agreement, or engage in behavior that is objectionable or unlawful, we may take
          action</strong> – including removing your content, suspending or terminating your account, or reporting you to
          law enforcement – as detailed below.
        </p>
        <ul>
          <li><strong>Removal of Content:</strong> The Company has the right (but not the obligation) to remove or block
            any User Content that, in our judgment, violates this EULA or is otherwise objectionable, without liability
            to you. This includes content that we find to be offensive, illegal, in violation of others’ rights, or that
            may expose us or our users to harm. We are <strong>not required to provide advance notice</strong> before
            removing content that violates our rules, but we may notify you after removal or take other appropriate
            steps.
          </li>
          <li><strong>Account Suspension or Termination:</strong> We may suspend or terminate your access to Comment8
            (including your user account and license to use the App) <strong>at any time, with or without prior
              notice</strong>, if we determine that you have violated this Agreement or engaged in behavior that is
            harmful to the community, other users, or the Company. In serious cases (for example, posting egregiously
            offensive or illegal content, or engaging in harassment), termination may be immediate and permanent. We are
            not liable for any loss of data or content resulting from termination. If your account is terminated, you
            must cease all use of the App and you will no longer be authorized to access our Services.
          </li>
          <li><strong>Reporting and Response:</strong> We provide in-app tools (such as a “Report” button or similar
            mechanism) for users to <strong>report objectionable content or abuse</strong>. If you encounter content or
            behavior that violates this EULA, please report it to us through the App.We will review reports of
            violations and take appropriate action in a timely manner. In compliance with Apple App Store and Google
            Play requirements, we strive to respond to and address valid complaints about objectionable content <strong>within
              24 hours</strong> of receiving notice. Repeated submission of false or malicious reports, however, is a
            violation of this EULA and may result in account action against the reporter.
          </li>
          <li><strong>User Blocking:</strong> Our App provides features that allow you to block or mute other users who
            you find abusive or with whom you do not wish to interact. We encourage users to utilize these features to
            tailor their experience. The Company may also <strong>block users who post objectionable content</strong> as
            part of our enforcement actions, especially if they commit serious or repeated violations.
          </li>
        </ul>

        <h3>6. Messaging and Following Feature</h3>
        <p>
          Comment8 allows users to <strong>follow</strong> each other and send direct messages (DMs) to mutual
          followers. By using the messaging feature, you agree to use it responsibly and <strong>not to send abusive,
          harassing, spam, or inappropriate messages</strong>. All conduct rules in Section 4 apply equally to your
          private communications. We do not actively monitor private messages, but we reserve the right to review
          messages if we receive reports of abuse or if required by law. If someone is harassing you via DMs, you should
          stop engaging and report the user to us. We may suspend messaging privileges or take other action if a user
          misuses the messaging tools.
        </p>
        <p>
          <strong>Privacy of Messages:</strong> Please note that while direct messages are generally only visible to the
          sender and recipient, we cannot guarantee complete confidentiality. By using the messaging service, you
          consent that we may store and access your communications as needed to investigate abuse, to ensure safety, or
          as otherwise described in our Privacy Policy. We will handle your personal data and communications in
          accordance with our Privacy Policy [link], which is incorporated by reference into this EULA.
        </p>

        <h3>7. Privacy and Data</h3>
        <p>
          Your privacy is important to us. Our collection, use, and disclosure of personal information in connection
          with the App are described in our <strong>Privacy Policy</strong>, which is available at <strong><a
          href="https://comment8.ai/privacy-policy">DMCA/Copyright
          Policy</a></strong>. By using Comment8, you agree that we can collect and use your information as outlined in
          the Privacy Policy. In particular, the App may need certain permissions (e.g., to show you notifications or to
          integrate with your web browser through our extension) – by granting those, you understand that certain data
          may be processed to provide the Services. If you do not agree with our data practices, please do not use the
          App.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of any personal information you share on Comment8.
          Avoid posting sensitive personal data (like your address, financial info, or identification numbers) in
          comments or messages. We are not responsible for any consequences if you choose to share personal information
          publicly on the platform.
        </p>

        <h3>8. Intellectual Property Rights in Content</h3>
        <p>
          Aside from your own User Content, all other content available through the App (including but not limited to
          text, images, graphics, logos, audio or video clips, software code, and compilations of content) is the
          property of the Company or its content suppliers and is protected by intellectual property laws. You <strong>must
          not use</strong> any of this proprietary content without authorization. This means you cannot copy,
          distribute, publicly display, publicly perform, or create derivative works from our content or other users’
          content except as enabled by the App’s normal functionality (for example, re-sharing a comment within the App
          interface, which may be allowed). Any unauthorized use of content not only breaches this EULA but may also
          violate copyright or other laws. If you believe that any content on Comment8 infringes your copyright or other
          intellectual property rights, please contact us through our <strong><a
          href="https://comment8.ai/support">support portal</a></strong> so we can address the issue.
        </p>

        <h3>9. Disclaimers of Warranties</h3>
        <p>
          <strong>Comment8 is provided to you “AS IS” and “AS AVAILABLE,” without warranties of any
            kind</strong> (either express or implied). To the fullest extent permitted by law, the Company disclaims all
          warranties, express, implied, or statutory, regarding the App and Services, including but not limited to
          any <strong>implied warranties of merchantability, fitness for a particular purpose, title, non-infringement,
          and any warranties arising from course of dealing or usage of trade</strong>. We do not guarantee that the App
          will meet your requirements or that its operation will be uninterrupted, secure, or error-free. We make no
          guarantees about the accuracy, reliability, or quality of any information (including User Content) you see on
          Comment8. <strong>Use of the App is at your own risk</strong>.
        </p>
        <p>
          In particular, while we strive to enforce the rules described in this EULA, <strong>we do not warrant or
          guarantee</strong> that all users will comply with these rules or that you will never encounter objectionable
          content. We are <strong>not responsible</strong> for the actions or content of users, and <strong>we provide
          no warranty</strong> that we will remove all content that violates our guidelines or that we will do so within
          any specific timeframe (though we will make good faith efforts as described in Section 5). The above
          disclaimers apply to the maximum extent permitted by applicable law.
        </p>

        <h3>10. Limitation of Liability</h3>
        <p>
          <strong>To the extent not prohibited by law, in no event will the Company or its officers, directors,
            employees, agents, affiliates, or licensors be liable for any indirect, incidental, special, consequential,
            or punitive damages</strong> (including damages for loss of profits, goodwill, data, or other intangible
          losses) arising out of or related to your use of (or inability to use) Comment8, regardless of the theory of
          liability (contract, tort, negligence, strict liability, or otherwise) and even if we have been advised of the
          possibility of such damages.
        </p>
        <p>
          To the maximum extent permitted by law, our <strong>total cumulative liability</strong> to you for any claims
          arising from or related to the App or this Agreement <strong>will not exceed the amount (if any) that you paid
          to us for use of the App or Services in the 12 months preceding the claim</strong> (or fifty US dollars, if
          you have not had any payment obligations to us). The existence of multiple claims will not enlarge this
          limit. <strong>Some jurisdictions do not allow certain liability exclusions or limits;</strong> in such
          jurisdictions, our liability will be limited to the minimum extent required by law.
        </p>
        <p>
          Nothing in this Agreement is intended to exclude or restrict any liability that <strong>cannot be lawfully
          excluded</strong>, such as liability for death or personal injury caused by our negligence, or for our
          fraudulent misrepresentation.
        </p>

        <h3>11. Indemnification</h3>
        <p>
          You agree to <strong>indemnify, defend, and hold harmless</strong> the Company and its officers, directors,
          employees, and agents from and against any and all claims, liabilities, damages, losses, and expenses
          (including reasonable attorneys’ fees and costs) arising out of or in any way connected with: (a) your access
          to or use of the App or Services; (b) your violation of any provision of this EULA; (c) your User Content,
          including any claim that your content infringes or misappropriates any third party’s rights or violates
          applicable law; or (d) your violation of any law or regulation. We reserve the right, at our own expense, to
          assume the exclusive defense and control of any matter subject to indemnification by you (without limiting
          your indemnification obligations), and in such case, you agree to cooperate with our defense of that claim.
        </p>

        <h3>12. Modifications to Service and Terms</h3>
        <p>
          <strong>Service Changes:</strong> We are continually improving and evolving Comment8. As such, we reserve the
          right to add, remove, suspend, or change features of the App or Services at any time. We may also impose
          limits on certain features or restrict your access to parts or all of the Service without notice or liability.
          We will endeavor to notify users of any material changes to the Service via in-app notification or on our
          website.
        </p>
        <p>
          <strong>Terms Changes:</strong> We may modify or update this EULA from time to time. If we make material
          changes, we will notify you by posting the updated Agreement in the App or through other
          communications. <strong>It is your responsibility to review this EULA periodically</strong>. Your continued
          use of Comment8 after any modifications indicates your acceptance of the new terms. If you do not agree to any
          amended terms, you should stop using the App and may terminate your account as described below.
        </p>

        <h3>13. Termination</h3>
        <p>
          This EULA is effective until terminated by either you or us. You may terminate this Agreement at any time
          by <strong>deleting the App from your devices and ceasing all use</strong> of Comment8 (if you have an
          account, you should also deactivate or delete your account through the App’s settings or by contacting
          support). The Company may terminate this EULA (and your access to the App) at any time without notice if you
          fail to comply with any term of this Agreement, or if the Company stops providing the Services. Upon any
          termination of this EULA, the license granted to you will immediately cease, and you must promptly delete or
          remove the App from your devices and destroy all copies.
        </p>
        <p>
          Sections of this EULA that by their nature should survive termination (such as ownership provisions, warranty
          disclaimers, limitations of liability, indemnity, and governing law) <strong>will continue to remain in
          effect</strong> after termination.
        </p>

        <h3>14. Governing Law and Dispute Resolution</h3>
        <p>
          This Agreement and any dispute arising out of or related to it or the App will be governed by and construed in
          accordance with the laws of the State of California, USA, without regard to its conflict of law
          principles. <strong>All claims or disputes</strong> arising in connection with this EULA or your use of
          Comment8 shall be <strong>subject to the exclusive jurisdiction</strong> of the state or federal courts
          located within San Francisco County, California, and you consent to personal jurisdiction and venue in such
          courts. <strong>However, we reserve the right to seek injunctive relief</strong> in any jurisdiction if
          necessary to prevent any unauthorized use or abuse of the App or violation of intellectual property rights.
        </p>
        <p>
          If you are a consumer residing in a jurisdiction with laws that give you the right to bring disputes in your
          home courts (or that otherwise override the above choice-of-law or venue clause), <strong>those rights may
          apply to you</strong> despite the above. This Agreement does not limit any consumer protection rights that you
          may be entitled to under the mandatory laws of your jurisdiction.
        </p>

        <h3>15. Miscellaneous</h3>
        <p>
          <strong>Severability:</strong> If any provision of this EULA is held to be invalid, illegal, or unenforceable
          by a court of competent jurisdiction, that provision will be enforced to the maximum extent permissible, and
          the remaining provisions of this Agreement will remain in full force and effect.
        </p>
        <p>
          <strong>No Waiver:</strong> Our failure to enforce any right or provision of this EULA will not be deemed a
          waiver of such right or provision. A waiver is only effective if in writing and signed by our authorized
          representative.
        </p>
        <p>
          <strong>Entire Agreement:</strong> This EULA (along with any additional terms we provide when you engage with
          certain features, and our Privacy Policy) constitutes the entire agreement between you and the Company
          regarding your use of Comment8, superseding any prior agreements or communications relating to the subject
          matter hereof.
        </p>
        <p>
          <strong>Assignment:</strong> You may not assign or transfer this Agreement (or any rights or obligations
          herein) without our prior written consent. We may assign this EULA freely to an affiliate or in connection
          with a merger, acquisition, or sale of assets, or by operation of law.
        </p>
        <p>
          <strong>Relationship:</strong> This Agreement does not create any joint venture, partnership, employment, or
          agency relationship between you and us. You may not make any representations on behalf of the Company.
        </p>
        <p>
          <strong>Headings:</strong> Section headings in this EULA are for convenience only and have no legal or
          contractual effect.
        </p>
        <p>
          <strong>Notifications:</strong> We may send you required legal notices and other communications about your
          account or the App electronically (for example, via email to any address you provided, or via in-app
          notification). By using the App, you consent to receive electronic communications from us. You may contact us
          at the email or address provided below for any questions or notices.
        </p>

        <h3>16. Contact Information</h3>
        <p>
          If you have any questions, concerns, or feedback about this EULA or the Comment8 App, please contact us at: <a
          href="https://comment8.ai/support">https://comment8.ai/support</a>
        </p>
      </div>
    </div>
  );
};
export default EULA
